import { getApiError } from '../utils/errorHelper';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { dashboardApi, adminApi, productsApi, ordersApi, dscConsentApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Loader2, Plus, Pencil, Save, X, Eye, Download, Upload, Check, XCircle,
  ChevronRight, ChevronDown, ArrowLeft, Search, Filter, Package, Library,
  FileText, Building2, Users, CreditCard, Calendar, Mail, Phone, User,
  CheckCircle, Clock, AlertCircle, FormInput, CalendarClock, AlertTriangle, Send,
  MessageSquare, Activity, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { toIST } from '../utils/dateUtils';

// Import admin components
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { DashboardOverview } from '../components/admin/DashboardOverview';
import { UsersTab } from '../components/admin/UsersTab';
import { CompaniesTab } from '../components/admin/CompaniesTab';
import GstCompliance from '../components/admin/GstCompliance';
import { DirectorsTab } from '../components/admin/DirectorsTab';
import { ShareholdersTab } from '../components/admin/ShareholdersTab';
import { OrdersTab } from '../components/admin/OrdersTab';
import DocumentTypesTab from '../components/admin/DocumentTypesTab';
import InformationFieldsTab from '../components/admin/InformationFieldsTab';
import ProductsTab from '../components/admin/ProductsTab';
import RecurringTab from '../components/admin/RecurringTab';
import LeadsTab from '../components/admin/LeadsTab';
import CRMTab from '../components/admin/CRMTab';
import InvoicesAdminTab from '../components/admin/InvoicesAdminTab';
import ChatLogsTab from '../components/admin/ChatLogsTab';
import TicketsTab from '../components/admin/TicketsTab';
import GSTFilingTab from '../components/admin/GSTFilingTab';
import OrderDetailView from '../components/admin/OrderDetailView';
import ExpiryTrackerPage from './ExpiryTrackerPage';
import SuperAdminTab from '../components/admin/SuperAdminTab';
import ActivityFeedTab from '../components/admin/ActivityFeedTab';
import NotificationBell from '../components/NotificationBell';
import ErrorBoundary from '../components/ErrorBoundary';
import { Menu } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getFileUrl = (fileUrl) => {
  const token = localStorage.getItem('token');
  if (!fileUrl) return '';
  return `${API_URL}${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${token}`;
};


// Filing Status Configuration (for recurring products: monthly, quarterly, yearly, custom)
const FILING_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'draft_in_progress', label: 'Draft in Progress' },
  { value: 'draft_sent_to_client', label: 'Draft Sent to Client' },
  { value: 'awaiting_client_approval', label: 'Awaiting Client Approval' },
  { value: 'client_approved', label: 'Client Approved' },
  { value: 'client_rejected', label: 'Client Rejected' },
  { value: 'filing_in_progress', label: 'Filing in Progress' },
  { value: 'filed_awaiting_acknowledgement', label: 'Filed — Awaiting Acknowledgement' },
  { value: 'filing_completed', label: 'Filing Completed' },
  { value: 'clarification_required', label: 'Clarification Required' },
  { value: 'clarification_submitted', label: 'Clarification Submitted' },
];

const FILING_STATUS_COLOR = {
  not_started: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  draft_in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  draft_sent_to_client: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  awaiting_client_approval: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  client_approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  client_rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  filing_in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  filed_awaiting_acknowledgement: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  filing_completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  clarification_required: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  clarification_submitted: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
};

// Filing Status Section Component (for recurring products)
const FilingStatusSection = ({ order, product, onUpdate, onRefresh }) => {
  const existingFS = order?.filing_status || {};
  const [fsForm, setFsForm] = useState({
    status: existingFS.status || 'not_started',
    remark: existingFS.remark || '',
    reference_number: existingFS.reference_number || '',
  });
  const [saving, setSaving] = useState(false);

  // Sync form with order data when it changes
  React.useEffect(() => {
    const fs = order?.filing_status || {};
    setFsForm({
      status: fs.status || 'not_started',
      remark: fs.remark || '',
      reference_number: fs.reference_number || '',
    });
  }, [order?.filing_status]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(fsForm);
      toast.success('Filing status updated');
      if (onRefresh) await onRefresh();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to update filing status'));
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = existingFS.status || 'not_started';
  const statusColor = FILING_STATUS_COLOR[currentStatus] || FILING_STATUS_COLOR.not_started;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
        <FileText className="w-5 h-5 text-indigo-400" />
        Filing Status
        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400">
          {product?.frequency_settings?.frequency || 'recurring'}
        </span>
      </h3>

      <div className="space-y-4">
        {/* Status selector */}
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Status</label>
          <select
            data-testid="admin-filing-status-select"
            value={fsForm.status}
            onChange={(e) => setFsForm(f => ({ ...f, status: e.target.value }))}
            className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-indigo-500"
          >
            {FILING_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Remark */}
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Remark <span className="normal-case text-slate-500">(optional)</span></label>
          <textarea
            data-testid="admin-filing-remark-input"
            value={fsForm.remark}
            onChange={(e) => setFsForm(f => ({ ...f, remark: e.target.value }))}
            placeholder="Add a note for the client..."
            rows={2}
            className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-indigo-500 placeholder:text-slate-500"
          />
        </div>

        {/* Reference Number */}
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Reference Number <span className="normal-case text-slate-500">(optional)</span></label>
          <input
            type="text"
            data-testid="admin-filing-reference-input"
            value={fsForm.reference_number}
            onChange={(e) => setFsForm(f => ({ ...f, reference_number: e.target.value }))}
            placeholder="e.g. GOV/2024/00123"
            className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-indigo-500 placeholder:text-slate-500"
          />
        </div>

        {/* Save button */}
        <div className="flex justify-end pt-1">
          <Button
            size="sm"
            data-testid="admin-filing-save-btn"
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5"
            onClick={handleSave}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

// Deadline Card Component for Admin

const REQUIREMENT_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'text_input', label: 'Text Input' },
  { value: 'file', label: 'File Upload' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'integer', label: 'Number' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
];

const REQUIREMENT_CATEGORIES = [
  { value: 'personal_kyc', label: 'Personal KYC' },
  { value: 'business_info', label: 'Business Information' },
  { value: 'auditor_appointment', label: 'Auditor Appointment' },
  { value: 'commencement_business', label: 'Commencement of Business' },
  { value: 'annual_compliance', label: 'Annual Compliance' },
  { value: 'director_change', label: 'Director Changes' },
  { value: 'director_kyc', label: 'Director KYC' },
  { value: 'capital_increase', label: 'Capital Increase' },
  { value: 'office_change', label: 'Office Change' },
  { value: 'share_transfer', label: 'Share Transfer' },
  { value: 'share_allotment', label: 'Share Allotment' },
  { value: 'general', label: 'General' },
];

const PRODUCT_CATEGORIES = [
  { value: 'incorporation', label: 'Incorporation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'post_incorporation', label: 'Post Incorporation' },
  { value: 'annual_compliance', label: 'Annual Compliance' },
  { value: 'event_based', label: 'Event Based' },
  { value: 'changes', label: 'Changes' },
  { value: 'annual_filing', label: 'Annual Filing' },
  { value: 'other', label: 'Other' },
];

const AdminDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { orderId: urlOrderId } = useParams();
  
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Users & Companies state
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [companyDirectors, setCompanyDirectors] = useState([]);
  const [companyShareholders, setCompanyShareholders] = useState([]);
  const [companyOrders, setCompanyOrders] = useState([]);
  const [companyOwner, setCompanyOwner] = useState(null);
  
  // Directors & Shareholders state
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [selectedShareholder, setSelectedShareholder] = useState(null);
  const [directorCompanies, setDirectorCompanies] = useState([]);
  const [shareholderCompany, setShareholderCompany] = useState(null);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  
  // Order detail state
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [uploadingAck, setUploadingAck] = useState(false);
  const [ackDocName, setAckDocName] = useState('');
  const [ackFileName, setAckFileName] = useState('');
  const ackFileRef = useRef(null);
  const signatureFileRef = useRef(null);
  const [signatureCaption, setSignatureCaption] = useState('');
  
  // Mock Payment state
  const [signatureFileName, setSignatureFileName] = useState('');
  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Sprint 3 — Activity feed unread badge. Polled every 60s per spec.
  const [activityUnreadCount, setActivityUnreadCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get('/activities/unread-count');
        if (!cancelled) setActivityUnreadCount(r.data?.count || 0);
      } catch {
        // silent — badge is decorative
      }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  // Recount when the user leaves the Activity tab (they may have marked things read).
  useEffect(() => {
    if (activeTab !== 'activity') {
      api.get('/activities/unread-count')
        .then(r => setActivityUnreadCount(r.data?.count || 0))
        .catch(() => { /* silent */ });
    }
  }, [activeTab]);

  // Track which tabs have been loaded to avoid loading flash
  const loadedTabs = useRef({});

  // Deep-link support: ?tab=tickets
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      const isFirstLoad = !loadedTabs.current[activeTab];
      try {
        if (activeTab === 'users') {
          if (isFirstLoad) setLoading(true);
          const data = await adminApi.getUsers();
          setUsers(data);
        } else if (activeTab === 'companies') {
          if (isFirstLoad) setLoading(true);
          const data = await adminApi.getCompanies();
          setCompanies(data);
        } else if (activeTab === 'directors') {
          if (isFirstLoad) setLoading(true);
          const data = await adminApi.getDirectors();
          setDirectors(data);
        } else if (activeTab === 'shareholders') {
          if (isFirstLoad) setLoading(true);
          const data = await adminApi.getShareholders();
          setShareholders(data);
        } else if (activeTab === 'orders') {
          // OrdersTab manages its own loading
          setLoading(false);
          return;
        } else if (activeTab === 'products') {
          setLoading(false);
          return;
        } else if (activeTab === 'document-types') {
          setLoading(false);
          return;
        } else if (activeTab === 'information-fields') {
          setLoading(false);
          return;
        }
        loadedTabs.current[activeTab] = true;
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab]);

  // Fetch user companies when user is selected
  useEffect(() => {
    const fetchUserCompanies = async () => {
      if (selectedUser) {
        try {
          const data = await adminApi.getUserCompanies(selectedUser.id);
          setUserCompanies(data);
        } catch (error) {
          setUserCompanies([]);
        }
      }
    };
    fetchUserCompanies();
  }, [selectedUser]);

  // Fetch company details when company is selected
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (selectedCompany) {
        try {
          const details = await adminApi.getCompanyDetails(selectedCompany.id);
          setCompanyDirectors(details.directors || []);
          setCompanyShareholders(details.shareholders || []);
          setCompanyOrders(details.orders || []);
          setCompanyOwner(details.user || null);
        } catch (error) {
          setCompanyDirectors([]);
          setCompanyShareholders([]);
          setCompanyOrders([]);
          setCompanyOwner(null);
        }
      }
    };
    fetchCompanyDetails();
  }, [selectedCompany]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSelectedCompany(null);
  };

  const handleSelectCompanyFromUser = (company) => {
    setSelectedCompany(company);
    setActiveTab('companies'); // Navigate to companies tab
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
  };

  const handleSelectDirectorFromList = (director) => {
    setSelectedDirector(director);
    setActiveTab('directors'); // Navigate to directors tab
  };

  const handleSelectShareholderFromList = (shareholder) => {
    // Navigate to shareholders tab with selected shareholder
    setActiveTab('shareholders');
  };

  const handleSelectOrderFromList = (order) => {
    handleViewOrderDetail(order.id);
    setActiveTab('orders'); // Navigate to orders tab
  };

  const handleBackFromCompany = () => {
    setSelectedCompany(null);
  };

  // Director handlers
  const handleSelectDirector = async (director) => {
    setSelectedDirector(director);
    try {
      const data = await adminApi.getDirectorDetails(director.id);
      setDirectorCompanies(data.companies || []);
    } catch (error) {
      setDirectorCompanies(director.companies || []);
    }
  };

  const handleRefreshDirectors = async () => {
    try {
      const data = await adminApi.getDirectors();
      setDirectors(data);
      if (selectedDirector) {
        const updated = data.find(d => d.id === selectedDirector.id);
        if (updated) {
          setSelectedDirector(updated);
          setDirectorCompanies(updated.companies || []);
        }
      }
    } catch (error) {
      console.error('Failed to refresh directors');
    }
  };

  // Shareholder handlers
  const handleSelectShareholder = async (shareholder) => {
    setSelectedShareholder(shareholder);
    try {
      const data = await adminApi.getShareholderDetails(shareholder.id);
      setShareholderCompany(data.company || null);
    } catch (error) {
      setShareholderCompany(shareholder.company || null);
    }
  };

  const handleRefreshShareholders = async () => {
    try {
      const data = await adminApi.getShareholders();
      setShareholders(data);
      if (selectedShareholder) {
        const updated = data.find(s => s.id === selectedShareholder.id);
        if (updated) {
          setSelectedShareholder(updated);
          setShareholderCompany(updated.company || null);
        }
      }
    } catch (error) {
      console.error('Failed to refresh shareholders');
    }
  };

  const handleViewOrderDetail = async (orderId) => {
    try {
      const data = await adminApi.getOrderDetails(orderId);
      setOrderDetail(data);
      setSelectedOrder(data.order);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  // Deep-link support: if URL is /admin/orders/:orderId, auto-open that order on mount
  // and whenever urlOrderId changes (browser back/forward, Asana link click, etc.).
  useEffect(() => {
    if (!urlOrderId) return;
    if (orderDetail?.order?.id === urlOrderId) return;
    setActiveTab('orders');
    handleViewOrderDetail(urlOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrderId]);

  // Order review functions
  const handleReviewSubmission = async (submissionId, status) => {
    try {
      if (status === 'approved') {
        await adminApi.approveSubmission(selectedOrder.id, submissionId);
      } else {
        await adminApi.rejectSubmission(selectedOrder.id, submissionId, reviewComment);
      }
      toast.success(`Document ${status}`);
      setReviewingSubmission(null);
      setReviewComment('');
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  const handleUpdateOrderStatus = async (newStatus) => {
    try {
      await adminApi.updateOrderStatus(selectedOrder.id, newStatus);
      toast.success('Order status updated');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUploadAcknowledgement = async () => {
    const file = ackFileRef.current?.files?.[0];
    if (!file || !ackDocName) {
      toast.error('Please provide document name and file');
      return;
    }
    setUploadingAck(true);
    try {
      await adminApi.uploadAcknowledgement(selectedOrder.id, ackDocName, file);
      toast.success('Acknowledgement uploaded');
      setAckDocName('');
      setAckFileName('');
      if (ackFileRef.current) ackFileRef.current.value = '';
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error('Failed to upload acknowledgement');
    } finally {
      setUploadingAck(false);
    }
  };

  const handleUploadSignatureDocument = async () => {
    const file = signatureFileRef.current?.files?.[0];
    if (!file || !signatureCaption) {
      toast.error('Please provide caption and file');
      return;
    }
    setUploadingSignature(true);
    try {
      await adminApi.uploadSignatureDocument(selectedOrder.id, signatureCaption, file);
      toast.success('Signature document uploaded');
      setSignatureCaption('');
      setSignatureFileName('');
      if (signatureFileRef.current) signatureFileRef.current.value = '';
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error('Failed to upload signature document');
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleCompleteOrder = async () => {
    try {
      await adminApi.completeOrder(selectedOrder.id);
      toast.success('Order marked as completed');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error('Failed to complete order');
    }
  };

  // Invoice flow handlers
  const handleSendInvoice = async () => {
    try {
      await adminApi.sendInvoice(selectedOrder.id);
      toast.success('Invoice marked as sent to client');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to send invoice'));
    }
  };

  const handleMarkPaymentReceived = async () => {
    try {
      await adminApi.markPaymentReceived(selectedOrder.id);
      toast.success('Payment marked as received');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to mark payment received'));
    }
  };

  const handleActivateDocuments = async () => {
    try {
      await adminApi.activateDocuments(selectedOrder.id);
      toast.success('Document submission activated for client');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to activate documents'));
    }
  };

  const handleMockPayment = async (remark = '') => {
    try {
      await adminApi.mockPayment(selectedOrder.id, remark);
      toast.success('Mock payment recorded - order is now marked as paid');
      handleViewOrderDetail(selectedOrder.id);
      const ordersData = await adminApi.getOrders();
      setOrders(ordersData);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to process mock payment'));
    }
  };

  const handleReviewSignedDocument = async (docId, status, remarkOverride = null) => {
    try {
      if (status === 'approved') {
        await adminApi.approveSignedDocument(selectedOrder.id, docId);
      } else {
        const remark = remarkOverride !== null ? remarkOverride : reviewComment;
        await adminApi.rejectSignedDocument(selectedOrder.id, docId, remark);
      }
      toast.success(`Signed document ${status}`);
      setReviewingSubmission(null);
      setReviewComment('');
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to review signed document'));
    }
  };

  const handleDeleteSignatureDocument = async (docId) => {
    try {
      await adminApi.deleteSignatureDocument(selectedOrder.id, docId);
      toast.success('Signature document deleted. Reminder emails for it will stop.');
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete signature document'));
    }
  };

  const handleDeleteAcknowledgement = async (docId) => {
    try {
      await adminApi.deleteAcknowledgement(selectedOrder.id, docId);
      toast.success('Acknowledgement / certificate deleted.');
      handleViewOrderDetail(selectedOrder.id);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete acknowledgement'));
    }
  };

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedUser(null);
    setSelectedCompany(null);
    setSelectedOrder(null);
    setOrderDetail(null);
    setSelectedDirector(null);
    setSelectedShareholder(null);
    setDirectorCompanies([]);
    setShareholderCompany(null);
  };

  return (
    <div className="admin-shell">
      {/* Topbar */}
      <div className="admin-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden" style={{ background: 'none', border: 'none', color: 'var(--admin-text-dim)', cursor: 'pointer', padding: 4 }}>
            <Menu style={{ width: 20, height: 20 }} />
          </button>
          <Link to="/" className="admin-topbar-brand">Snigdha360</Link>
          <div className="admin-sep hidden lg:block" />
          <span style={{ fontSize: 13, color: 'var(--admin-text-soft)' }} className="hidden lg:inline">
            Welcome, <span style={{ color: 'var(--admin-text)', fontWeight: 500 }}>{user?.full_name || 'Admin'}</span>
          </span>
        </div>
        <div className="admin-topbar-right">
          <span className="admin-badge"><span className="admin-badge-dot" />{user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</span>
          <NotificationBell />
        </div>
      </div>

      {/* Layout Grid */}
      <div className="admin-layout">
        {/* Sidebar */}
        <AdminSidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          user={user}
          onLogout={handleLogout}
          mobileOpen={mobileMenuOpen}
          setMobileOpen={setMobileMenuOpen}
          activityUnreadCount={activityUnreadCount}
        />

        {/* Main Content */}
        <main className="admin-main">

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="admin-page-head">
              <div>
                <h1 className="admin-page-title">Users</h1>
                <p className="admin-page-subtitle">Manage platform users and their companies</p>
              </div>
            </div>
            <UsersTab 
              users={users}
              loading={loading}
              onSelectUser={handleSelectUser}
              selectedUser={selectedUser}
              userCompanies={userCompanies}
              onSelectCompany={handleSelectCompanyFromUser}
              onSelectOrder={(order) => {
                handleViewOrderDetail(order.id);
                setActiveTab('orders');
              }}
              onUserUpdated={() => {
                // Refresh users list after update
                setActiveTab('users');
              }}
              token={localStorage.getItem('token')}
            />
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div>
            <div className="admin-page-head">
              <div>
                <h1 className="admin-page-title">Companies</h1>
                <p className="admin-page-subtitle">View all registered companies with directors and shareholders</p>
              </div>
            </div>
            <CompaniesTab 
              companies={companies}
              loading={loading}
              onSelectCompany={handleSelectCompany}
              selectedCompany={selectedCompany}
              directors={companyDirectors}
              shareholders={companyShareholders}
              orders={companyOrders}
              owner={companyOwner}
              onSelectDirector={(director) => {
                handleSelectDirector(director);
                setActiveTab('directors');
              }}
              onSelectShareholder={(shareholder) => {
                handleSelectShareholder(shareholder);
                setActiveTab('shareholders');
              }}
              onSelectOrder={(order) => {
                handleViewOrderDetail(order.id);
                setActiveTab('orders');
              }}
              onSelectOwner={(owner) => {
                if (owner) {
                  setSelectedUser(owner);
                  setActiveTab('users');
                }
              }}
              onBack={null}
              onRefresh={() => setActiveTab('companies')}
            />
          </div>
        )}

        {/* Directors Tab */}
        {activeTab === 'directors' && (
          <div>
            <div className="admin-page-head">
              <div>
                <h1 className="admin-page-title">Directors</h1>
                <p className="admin-page-subtitle">View all directors and their linked companies</p>
              </div>
            </div>
            <DirectorsTab 
              directors={directors}
              loading={loading}
              onSelectDirector={handleSelectDirector}
              selectedDirector={selectedDirector}
              directorCompanies={directorCompanies}
              currentUser={user}
              onRefresh={handleRefreshDirectors}
              onNavigateToCompany={(company) => {
                setSelectedCompany(company);
                setSelectedDirector(null);
                setActiveTab('companies');
              }}
              onNavigateToShareholder={(shareholder) => {
                handleSelectShareholder(shareholder);
                setSelectedDirector(null);
                setActiveTab('shareholders');
              }}
              onNavigateToOrder={(orderId) => {
                handleViewOrderDetail(orderId);
                setSelectedDirector(null);
              }}
            />
          </div>
        )}

        {/* Shareholders Tab */}
        {activeTab === 'shareholders' && (
          <div>
            <div className="admin-page-head">
              <div>
                <h1 className="admin-page-title">Shareholders</h1>
                <p className="admin-page-subtitle">View all shareholders and their shareholding details</p>
              </div>
            </div>
            <ShareholdersTab 
              shareholders={shareholders}
              loading={loading}
              onSelectShareholder={handleSelectShareholder}
              selectedShareholder={selectedShareholder}
              shareholderCompany={shareholderCompany}
              onRefresh={handleRefreshShareholders}
              onNavigateToCompany={(company) => {
                setSelectedCompany(company);
                setSelectedShareholder(null);
                setActiveTab('companies');
              }}
              onNavigateToDirector={(director) => {
                handleSelectDirector(director);
                setSelectedShareholder(null);
                setActiveTab('directors');
              }}
              onNavigateToOrder={(orderId) => {
                handleViewOrderDetail(orderId);
                setSelectedShareholder(null);
              }}
            />
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && !orderDetail && (
          <div>
            <div className="admin-page-head">
              <div>
                <h1 className="admin-page-title">Orders</h1>
                <p className="admin-page-subtitle">Activity feed and order management</p>
              </div>
            </div>
            <OrdersTab 
              onViewOrderDetail={handleViewOrderDetail}
              currentUser={user}
            />
          </div>
        )}

        {/* Order Detail View */}
        {activeTab === 'orders' && orderDetail && (
          <ErrorBoundary>
          <OrderDetailView
            currentUser={user} 
            orderDetail={orderDetail}
            onBack={() => {
              setOrderDetail(null);
              setSelectedOrder(null);
              // If we arrived via deep link, return to the dashboard URL.
              if (urlOrderId) navigate('/admin');
            }}
            onReviewSubmission={handleReviewSubmission}
            onUpdateStatus={handleUpdateOrderStatus}
            onUploadAck={handleUploadAcknowledgement}
            uploadingAck={uploadingAck}
            ackDocName={ackDocName}
            setAckDocName={setAckDocName}
            ackFileRef={ackFileRef}
            ackFileName={ackFileName}
            setAckFileName={setAckFileName}
            onCompleteOrder={handleCompleteOrder}
            onSendInvoice={handleSendInvoice}
            onMarkPaymentReceived={handleMarkPaymentReceived}
            onActivateDocuments={handleActivateDocuments}
            onMockPayment={handleMockPayment}
            reviewingSubmission={reviewingSubmission}
            setReviewingSubmission={setReviewingSubmission}
            reviewComment={reviewComment}
            setReviewComment={setReviewComment}
            signatureCaption={signatureCaption}
            setSignatureCaption={setSignatureCaption}
            signatureFileRef={signatureFileRef}
            signatureFileName={signatureFileName}
            setSignatureFileName={setSignatureFileName}
            onUploadSignatureDocument={handleUploadSignatureDocument}
            uploadingSignature={uploadingSignature}
            onReviewSignedDocument={handleReviewSignedDocument}
            onDeleteSignatureDocument={handleDeleteSignatureDocument}
            onDeleteAcknowledgement={handleDeleteAcknowledgement}
            onRefreshOrder={async () => {
              const data = await adminApi.getOrderDetails(orderDetail.order.id);
              setOrderDetail(data);
            }}
            onNavigateToCompany={(company) => {
              setSelectedCompany(company);
              setOrderDetail(null);
              setActiveTab('companies');
            }}
            onNavigateToUser={(user) => {
              setSelectedUser(user);
              setOrderDetail(null);
              setActiveTab('users');
            }}
          />
          </ErrorBoundary>
        )}

        {/* Products Tab - NEW Product Engine */}
        {activeTab === 'products' && (
          <ProductsTab />
        )}

        {/* Document Types Tab - NEW */}
        {activeTab === 'document-types' && (
          <DocumentTypesTab />
        )}

        {/* Information Fields Tab - NEW */}
        {activeTab === 'information-fields' && (
          <InformationFieldsTab />
        )}

        {/* Recurring Orders Tab */}
        {activeTab === 'recurring' && (
          <RecurringTab />
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <LeadsTab />
        )}

        {/* CRM Tab — Contacts + Leads */}
        {activeTab === 'crm' && (
          <CRMTab />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <InvoicesAdminTab />
        )}

        {/* GST Compliance Tab */}
        {activeTab === 'gst-compliance' && (
          <GstCompliance />
        )}

        {/* Chat Logs Tab */}
        {activeTab === 'chatlogs' && (
          <ChatLogsTab />
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <TicketsTab
            onNavigateToCompany={(company) => {
              setSelectedCompany(company);
              setActiveTab('companies');
            }}
            onNavigateToOrder={(orderId) => {
              handleViewOrderDetail(orderId);
              setActiveTab('orders');
            }}
            onNavigateOrderFromActivity={(orderId) => {
              if (!orderId) return;
              navigate(`/admin/orders/${orderId}`);
            }}
          />
        )}

        {/* Expiry Tracker Tab */}
        {activeTab === 'expiry-tracker' && (
          <ExpiryTrackerPage
            onNavigateToCompany={(company) => {
              setSelectedCompany(company);
              setActiveTab('companies');
            }}
            onNavigateToDirector={(directorId, companyId) => {
              // Navigate to companies tab with that company selected
              if (companyId) {
                setSelectedCompany({ id: companyId });
                setActiveTab('companies');
              }
            }}
          />
        )}

        {/* Super Admin Tab */}
        {activeTab === 'super-admin' && user?.role === 'super_admin' && (
          <SuperAdminTab />
        )}

        </main>
      </div>
    </div>
  );
};


// Products Tab Content
const ProductsTabContent = ({ 
  products, requirements, loading, showProductForm, setShowProductForm,
  editingProduct, setEditingProduct, productForm, setProductForm,
  handleCreateProduct, handleUpdateProduct, expandedProduct, setExpandedProduct,
  productRequirements, fetchProductRequirements, showLinkReqModal, setShowLinkReqModal,
  selectedReqToLink, setSelectedReqToLink, handleLinkRequirementToProduct,
  handleUnlinkRequirement, linkingReq
}) => {
  const getUnlinkedRequirements = (productId) => {
    const linkedReqs = productRequirements[productId] || [];
    const linkedIds = linkedReqs.map(r => r.id);
    return requirements.filter(r => !linkedIds.includes(r.id));
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Products</h1>
          <p className="text-slate-400">Manage compliance products and services</p>
        </div>
        <Button onClick={() => setShowProductForm(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {/* Product Form Modal */}
      {(showProductForm || editingProduct) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {editingProduct ? 'Edit Product' : 'Create New Product'}
            </h3>
            <div className="space-y-4">
              <Input placeholder="Product Name" value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white" />
              <Textarea placeholder="Description" value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white" rows={3} />
              <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PRODUCT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-slate-700">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Input type="number" placeholder="Govt Fee" value={productForm.govt_fee}
                  onChange={(e) => setProductForm({...productForm, govt_fee: parseFloat(e.target.value) || 0})}
                  className="bg-slate-900 border-slate-700 text-white" />
                <Input type="number" placeholder="Professional Fee" value={productForm.professional_fee}
                  onChange={(e) => setProductForm({...productForm, professional_fee: parseFloat(e.target.value) || 0})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <Input placeholder="Time to Complete (e.g., 7 days)" value={productForm.time_to_complete}
                onChange={(e) => setProductForm({...productForm, time_to_complete: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white" />
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="ghost" onClick={() => { setShowProductForm(false); setEditingProduct(null); }}
                className="text-slate-400">Cancel</Button>
              <Button onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                className="bg-indigo-600 hover:bg-indigo-700">
                {editingProduct ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-slate-700 rounded w-full mb-4"></div>
              <div className="h-8 bg-slate-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {products.map((product) => (
            <div key={product.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <div className="p-5 flex items-start justify-between cursor-pointer hover:bg-slate-800/80 transition-colors"
                onClick={() => {
                  if (expandedProduct === product.id) {
                    setExpandedProduct(null);
                  } else {
                    setExpandedProduct(product.id);
                    if (!productRequirements[product.id]) {
                      fetchProductRequirements(product.id);
                    }
                  }
                }}>
                <div>
                  <h3 className="text-white font-semibold text-lg">{product.name}</h3>
                  <p className="text-slate-400 text-sm mt-1 line-clamp-2">{product.description}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <span className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-400 capitalize">
                      {product.category?.replace('_', ' ')}
                    </span>
                    <span className="text-emerald-400 font-semibold">
                      ₹{(product.total_price || product.price || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={(e) => {
                    e.stopPropagation();
                    setEditingProduct(product);
                    setProductForm({
                      name: product.name, description: product.description || '', category: product.category,
                      sub_category: product.sub_category || '', govt_fee: product.govt_fee || 0,
                      professional_fee: product.professional_fee || 0, time_to_complete: product.time_to_complete || '',
                      is_recurring: product.is_recurring || false
                    });
                  }} className="text-slate-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedProduct === product.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Requirements */}
              {expandedProduct === product.id && (
                <div className="border-t border-slate-700/50 p-5 bg-slate-900/30">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-medium">Linked Requirements</h4>
                    <Button size="sm" variant="outline" onClick={() => setShowLinkReqModal(product.id)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700">
                      <Plus className="w-4 h-4 mr-1" /> Link Requirement
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(productRequirements[product.id] || []).map((req) => (
                      <div key={req.id} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                        <div>
                          <p className="text-white text-sm">{req.label}</p>
                          <p className="text-slate-500 text-xs capitalize">{req.type?.replace('_', ' ')} • {req.category?.replace('_', ' ')}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleUnlinkRequirement(product.id, req.id)}
                          className="text-red-400 hover:bg-red-500/20 h-7">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {(!productRequirements[product.id] || productRequirements[product.id].length === 0) && (
                      <p className="text-slate-500 text-sm text-center py-4">No requirements linked</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Link Requirement Modal */}
      {showLinkReqModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Link Requirement</h3>
            <Select value={selectedReqToLink} onValueChange={setSelectedReqToLink}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Select requirement" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                {getUnlinkedRequirements(showLinkReqModal).map((req) => (
                  <SelectItem key={req.id} value={req.id} className="text-white hover:bg-slate-700">
                    {req.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="ghost" onClick={() => { setShowLinkReqModal(null); setSelectedReqToLink(''); }}
                className="text-slate-400">Cancel</Button>
              <Button onClick={() => handleLinkRequirementToProduct(showLinkReqModal)} disabled={linkingReq}
                className="bg-indigo-600 hover:bg-indigo-700">
                {linkingReq ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Requirements Tab Content
const RequirementsTabContent = ({ 
  requirements, loading, showReqForm, setShowReqForm,
  editingReq, setEditingReq, reqForm, setReqForm,
  handleCreateRequirement, handleUpdateRequirement
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const filteredRequirements = requirements.filter(req => {
    const matchesSearch = req.label?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || req.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Requirements Library</h1>
          <p className="text-slate-400">Manage document and information requirements</p>
        </div>
        <Button onClick={() => setShowReqForm(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Add Requirement
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input placeholder="Search requirements..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Filter category" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white hover:bg-slate-700">All Categories</SelectItem>
            {REQUIREMENT_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-slate-700">
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Requirement Form Modal */}
      {(showReqForm || editingReq) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {editingReq ? 'Edit Requirement' : 'Create New Requirement'}
            </h3>
            <div className="space-y-4">
              <Input placeholder="Label" value={reqForm.label}
                onChange={(e) => setReqForm({...reqForm, label: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white" />
              <Textarea placeholder="Description" value={reqForm.description}
                onChange={(e) => setReqForm({...reqForm, description: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white" rows={3} />
              <Select value={reqForm.type} onValueChange={(v) => setReqForm({...reqForm, type: v})}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {REQUIREMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={reqForm.category} onValueChange={(v) => setReqForm({...reqForm, category: v})}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {REQUIREMENT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-slate-700">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="ghost" onClick={() => { setShowReqForm(false); setEditingReq(null); }}
                className="text-slate-400">Cancel</Button>
              <Button onClick={editingReq ? handleUpdateRequirement : handleCreateRequirement}
                className="bg-indigo-600 hover:bg-indigo-700">
                {editingReq ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Requirements Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-slate-700 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRequirements.map((req) => (
            <div key={req.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-slate-600 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-white font-medium line-clamp-2">{req.label}</h3>
                <Button variant="ghost" size="sm" onClick={() => {
                  setEditingReq(req);
                  setReqForm({
                    label: req.label, description: req.description || '', type: req.type || req.field_type,
                    category: req.category, is_entity_level: req.is_entity_level || false
                  });
                }} className="text-slate-400 hover:text-white -mr-2 -mt-2">
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-slate-500 text-sm line-clamp-2 mb-3">{req.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400 capitalize">
                  {(req.type || req.field_type)?.replace('_', ' ')}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400 capitalize">
                  {req.category?.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
          {filteredRequirements.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              No requirements found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
